import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Check } from 'lucide-react';
import type { PlanDefinition, PlanTier } from '@/api-types';

interface PlanCardProps {
	plan: PlanDefinition;
	currentTier?: PlanTier | null;
	onSelect: (plan: PlanDefinition) => void;
	isAuthenticated: boolean;
	disabled?: boolean;
}

export function PlanCard({ plan, currentTier, onSelect, isAuthenticated, disabled }: PlanCardProps) {
	const isCurrent = currentTier === plan.tier;
	const isPopular = plan.tier === 'pro';

	const priceDisplay = plan.priceCents === null
		? 'Custom'
		: plan.priceCents === 0
			? '$0'
			: `$${(plan.priceCents / 100).toFixed(0)}`;

	const tierRank: Record<PlanTier, number> = { free: 0, pro: 1, team: 2, enterprise: 3 };
	const isDowngrade = currentTier != null && tierRank[plan.tier] < tierRank[currentTier];

	const buttonLabel = isCurrent
		? 'Current Plan'
		: plan.isContactSales
			? 'Contact Sales'
			: isAuthenticated
				? isDowngrade ? 'Downgrade' : 'Upgrade'
				: 'Get Started';

	return (
		<Card className={`relative flex flex-col ${isPopular ? 'border-blue-500 border-2' : ''}`}>
			{isPopular && (
				<div className="absolute -top-3 left-1/2 -translate-x-1/2">
					<Badge className="bg-blue-500 text-white">Most Popular</Badge>
				</div>
			)}
			<CardHeader className="pb-4">
				<CardTitle className="text-lg">{plan.name}</CardTitle>
				<div className="mt-2">
					<span className="text-3xl font-bold">{priceDisplay}</span>
					{plan.priceCents !== null && plan.priceCents > 0 && (
						<span className="text-text-tertiary text-sm">
							/{plan.isPerSeat ? 'seat/' : ''}mo
						</span>
					)}
				</div>
			</CardHeader>
			<CardContent className="flex-1 flex flex-col">
				<ul className="space-y-2 flex-1 mb-6">
					{plan.features.map((feature) => (
						<li key={feature} className="flex items-start gap-2 text-sm">
							<Check className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
							<span>{feature}</span>
						</li>
					))}
				</ul>
				<Button
					onClick={() => onSelect(plan)}
					disabled={isCurrent || disabled}
					variant={isPopular ? 'default' : 'outline'}
					className="w-full"
				>
					{buttonLabel}
				</Button>
			</CardContent>
		</Card>
	);
}
